/**
 * Staff management page.
 *
 * Issue #31 — owners can:
 *   - View the team list with role, status (active/inactive), and last
 *     login.
 *   - Invite a new staff member (email + first/last + role). Server
 *     creates a TenantUser, dispatches a magic-link email; the invitee
 *     sets their own password and a StaffUser row is bridged on
 *     accept.
 *   - Deactivate (or reactivate) a staff member. Deactivation flips
 *     isActive=false on the server and bumps sessionInvalidatedAt so
 *     the user is locked out at the next request.
 *
 * Owner/admin RBAC mirrors the server: any signed-in staff can view;
 * only owners/admins/platform_admins can invite, update role, or
 * deactivate.
 *
 * #221 Wave 1 (2026-06-10): converted from inert Tailwind utilities
 * (admin-app has no Tailwind build — the page rendered "jumbled") to
 * the ferro-* design system: real table chrome, tonal role pills with
 * human labels (never the raw slug), status pills, pill-shaped role
 * selects, skeleton loading, styled alert banners, and working mobile
 * cards at <768px.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientConfig } from '@ferro/config';
import { Modal, PageHeader } from '@ferro/ui';
import {
  adminStaffApi,
  type AdminPendingInvite,
  type AdminStaffRolesResponse,
  type AdminStaffUser,
} from '../lib/api.js';
import { useAuth } from '../contexts/AuthContext.js';
import { StaffForm, type StaffFormValues } from '../components/staff/StaffForm.js';
import {
  ROLES,
  type RoleSlug,
  effectiveRolesOf,
  isGrantableRoleSlug,
  rolesHavePermission,
} from '../lib/rbac-matrix.js';

/** Human-readable labels for the canonical tenant roles. */
const ROLE_LABELS: Record<RoleSlug, string> = {
  owner: 'Owner',
  manager: 'Manager',
  receptionist: 'Receptionist',
  walker: 'Walker',
  groomer: 'Groomer',
  kennel_tech: 'Kennel Tech',
  accountant: 'Accountant',
};

// Role dropdown options are DERIVED from the canonical matrix (rbac-matrix.ts),
// so the Staff page can no longer drift from the server's allow-list. The
// previous hand-maintained list was missing 'receptionist' + 'walker' and
// carried the legacy 'staff'/'admin' roles. platform_admin is intentionally
// absent — it's a control-plane role, not tenant-assignable.
//
// #133/#134/#135/#290 — walker / groomer / kennel_tech / accountant now have
// dedicated role-home screens (/walker, /kennel, /groomer, /accountant) so
// staff invited with these roles land on a working surface rather than an
// empty dashboard. All four are promoted from pilot-blocked to assignable.
// ROLE_LABELS stays full so legacy rows with any role still render their label.
const PILOT_ASSIGNABLE_ROLES: RoleSlug[] = ['owner', 'manager', 'receptionist', 'walker', 'groomer', 'kennel_tech', 'accountant'];
const ROLE_OPTIONS = ROLES.filter((value) =>
  PILOT_ASSIGNABLE_ROLES.includes(value),
).map((value) => ({ value, label: ROLE_LABELS[value] }));

// New staff are most often front-desk hires; default the invite to
// receptionist rather than the first option (owner) — never default an
// invite to the most-privileged role. `satisfies` pins it to a real role.
const DEFAULT_INVITE_ROLE = 'receptionist' satisfies RoleSlug;

/**
 * Tolerate non-canonical role strings (legacy rows, platform_admin) at
 * the display layer — they get the neutral base pill + the raw slug as
 * a fallback label rather than crashing.
 */
function roleLabel(role: string): string {
  return ROLE_LABELS[role as RoleSlug] ?? role;
}

function rolePillClass(role: string): string {
  const isCanonical = (ROLES as readonly string[]).includes(role);
  return isCanonical
    ? `ferro-role-pill ferro-role-pill--${role}`
    : 'ferro-role-pill';
}

/** A5 — expiry derives server-side from inviteSentAt + 7 days. */
function isInviteExpired(invite: AdminPendingInvite): boolean {
  if (!invite.invite_expires_at) return false;
  const t = new Date(invite.invite_expires_at).getTime();
  return !Number.isNaN(t) && t < Date.now();
}

/**
 * #788 — one honest line about the invite email's fate, from the send
 * record. Distinguishes "provider accepted it (check spam)" from "the
 * send failed (resend it)". Null for pre-#788 invites with no record;
 * the caller renders nothing rather than guessing.
 */
function lastSendLine(
  invite: AdminPendingInvite,
): { key: string; when: string; failed: boolean } | null {
  if (!invite.last_send_at || !invite.last_send_outcome) return null;
  const when = formatLastLogin(invite.last_send_at);
  if (invite.last_send_outcome === 'accepted') {
    return { key: 'admin.staff.lastSendAccepted', when, failed: false };
  }
  return { key: 'admin.staff.lastSendFailed', when, failed: true };
}

function formatLastLogin(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

type StaffProps = {
  config: ClientConfig;
};

export function Staff({ config }: StaffProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const { staff: currentStaff } = useAuth();
  const { t } = useTranslation();

  // Staff management (invite / change role / deactivate) is owner-only on
  // the server (rbac-admin:write:all). Derive the UI gate from the same
  // permission so a manager never sees a control the server would 403 —
  // mirrors the server's requireOwnerRole. platform_admin passes too.
  // #428 — union over primary + granted roles (owner is never grantable,
  // so this stays owner/platform_admin in practice).
  const canManageStaff = rolesHavePermission(
    effectiveRolesOf(currentStaff),
    'rbac-admin:write:all',
  );

  const [staffList, setStaffList] = useState<AdminStaffUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  // A5 — invited-but-not-yet-accepted members (TenantUser rows; no
  // StaffUser exists until the accept flow bridges one), each with a
  // working "Resend invite" action.
  const [pendingInvites, setPendingInvites] = useState<AdminPendingInvite[]>([]);
  const [invitesError, setInvitesError] = useState<string | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  // #666 — two-tap revoke, matching the kiosk-token revoke in
  // TimeclockAdminSection: touch-friendly confirm with no hover state and
  // no window.confirm. Revoking is destructive and irreversible (the
  // invite must be re-sent), so it must not be a single stray tap.
  const [revokeArmedId, setRevokeArmedId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  // #428 — Manage-roles editor (owner-only). `rolesFor` is the member whose
  // grants are being edited; the modal owns its own load/error/pending
  // states so the roster stays interactive behind it.
  const [rolesFor, setRolesFor] = useState<AdminStaffUser | null>(null);
  const [rolesData, setRolesData] = useState<AdminStaffRolesResponse | null>(null);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [rolesPendingSlug, setRolesPendingSlug] = useState<string | null>(null);
  const [addRoleValue, setAddRoleValue] = useState('');

  const loadStaff = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    const result = await adminStaffApi.getStaff(apiUrl);
    setIsLoading(false);
    if (result.error) {
      setError(result.error);
    } else if (result.data) {
      setStaffList(result.data.staff);
    }
  };

  const loadInvites = async (): Promise<void> => {
    // Owner-only endpoint; non-owners never reach this page (route
    // gating, #131) but don't fire a guaranteed-403 fetch regardless.
    if (!canManageStaff) return;
    setInvitesError(null);
    const result = await adminStaffApi.getPendingInvites(apiUrl);
    if (result.error) {
      setInvitesError(result.error);
    } else {
      setPendingInvites(result.data?.invites ?? []);
    }
  };

  useEffect(() => {
    void loadStaff();
    void loadInvites();
  }, []);

  const handleRoleChange = async (
    staffId: string,
    newRole: string,
  ): Promise<void> => {
    setRoleError(null);
    const result = await adminStaffApi.updateRole(apiUrl, staffId, newRole);
    if (result.data) {
      setStaffList((prev) =>
        prev.map((s) => (s.id === staffId ? { ...s, ...result.data! } : s)),
      );
    } else if (result.error) {
      setRoleError(result.error);
    }
  };

  const handleToggleActive = async (member: AdminStaffUser): Promise<void> => {
    if (member.id === currentStaff?.id) {
      setActionError('You cannot deactivate your own account');
      return;
    }
    setActionError(null);
    setPendingId(member.id);
    const nextActive = !member.is_active;
    const previous = staffList;
    // Optimistic update for snappy UI.
    setStaffList((prev) =>
      prev.map((s) =>
        s.id === member.id ? { ...s, is_active: nextActive } : s,
      ),
    );
    const result = await adminStaffApi.setActive(apiUrl, member.id, nextActive);
    setPendingId(null);
    if (result.error) {
      setActionError(result.error);
      setStaffList(previous);
    } else if (result.data) {
      setStaffList((prev) =>
        prev.map((s) => (s.id === member.id ? { ...s, ...result.data! } : s)),
      );
    }
  };

  const handleInvite = async (values: StaffFormValues): Promise<void> => {
    setIsSubmitting(true);
    setInviteError(null);
    setInviteSuccess(null);
    const result = await adminStaffApi.inviteStaff(apiUrl, values);
    setIsSubmitting(false);
    if (result.error) {
      setInviteError(result.error);
      return;
    }
    if (result.data) {
      setInviteSuccess(`Invite sent to ${result.data.email}`);
      setShowInviteForm(false);
      // Refresh both lists: the new invite appears in Pending invites
      // immediately (A5); the staff roster picks the member up once the
      // accept flow bridges a StaffUser.
      void loadStaff();
      void loadInvites();
    }
  };

  // A5 — reissue token + expiry and resend the branded email for an
  // invited (not yet accepted) member.
  const handleResendInvite = async (invite: AdminPendingInvite): Promise<void> => {
    setActionError(null);
    setInviteSuccess(null);
    setResendingEmail(invite.email);
    const result = await adminStaffApi.resendInvite(apiUrl, invite.email);
    setResendingEmail(null);
    if (result.error) {
      setActionError(result.error);
      return;
    }
    if (result.data) {
      setInviteSuccess(t('admin.staff.inviteResentTo', { email: invite.email }));
      // Fresh sent/expiry stamps: an Expired pill flips back to Invited.
      setPendingInvites((prev) =>
        prev.map((i) =>
          i.id === invite.id
            ? {
                ...i,
                invite_sent_at: result.data!.invite_sent_at,
                invite_expires_at: result.data!.invite_expires_at,
                // #788 — reflect the send the server just reported.
                last_send_at: result.data!.last_send_at ?? null,
                last_send_outcome: result.data!.last_send_outcome ?? null,
                last_send_provider_message_id:
                  result.data!.last_send_provider_message_id ?? null,
              }
            : i,
        ),
      );
    }
  };

  // #666 — kill a pending invite. The emailed magic link is a 7-day
  // BEARER credential for the invited role (owner, in the worst case);
  // sent to a typo'd address it was previously unrecoverable without
  // hand-written SQL against prod (#703).
  //
  // Deliberately NOT "resend to fix a typo": resend targets the same
  // email, so for a wrong address it just mails the stranger a fresh
  // credential. Revoke is the only action that closes the door.
  const handleRevokeInvite = async (
    invite: AdminPendingInvite,
  ): Promise<void> => {
    if (revokeArmedId !== invite.id) {
      setRevokeArmedId(invite.id);
      return;
    }
    setRevokeArmedId(null);
    setActionError(null);
    setInviteSuccess(null);
    setRevokingId(invite.id);
    const result = await adminStaffApi.revokeInvite(apiUrl, invite.id);
    setRevokingId(null);
    if (result.error) {
      setActionError(result.error);
      return;
    }
    setInviteSuccess(t('admin.staff.inviteRevokedFor', { email: invite.email }));
    // Drop it locally rather than refetching: the server moves the row
    // out of `invited`, so it is already gone from the pending list.
    setPendingInvites((prev) => prev.filter((i) => i.id !== invite.id));
  };

  // An armed destructive button must never linger waiting for an
  // accidental second tap (same 4s disarm as the kiosk revoke).
  useEffect(() => {
    if (revokeArmedId === null) return undefined;
    const id = window.setTimeout(() => setRevokeArmedId(null), 4_000);
    return () => window.clearTimeout(id);
  }, [revokeArmedId]);

  // ── #428 — additive multi-role grants (owner-only) ──────────────────

  const loadRoles = async (member: AdminStaffUser): Promise<void> => {
    setRolesLoading(true);
    setRolesError(null);
    const result = await adminStaffApi.getRoles(apiUrl, member.id);
    setRolesLoading(false);
    if (result.error) {
      setRolesError(result.error);
    } else if (result.data) {
      setRolesData(result.data);
    }
  };

  const openRolesEditor = (member: AdminStaffUser): void => {
    setRolesFor(member);
    setRolesData(null);
    setAddRoleValue('');
    void loadRoles(member);
  };

  const closeRolesEditor = (): void => {
    setRolesFor(null);
    setRolesData(null);
    setRolesError(null);
    setRolesPendingSlug(null);
    setAddRoleValue('');
  };

  /** Mirror a grant/revoke into the roster row so chips stay honest. */
  const syncMemberGrants = (staffId: string, grants: string[]): void => {
    setStaffList((prev) =>
      prev.map((s) => (s.id === staffId ? { ...s, granted_roles: grants } : s)),
    );
  };

  const handleGrantRole = async (member: AdminStaffUser): Promise<void> => {
    const roleSlug = addRoleValue;
    if (!isGrantableRoleSlug(roleSlug)) return;
    setRolesError(null);
    setRolesPendingSlug(roleSlug);
    const result = await adminStaffApi.grantRole(apiUrl, member.id, roleSlug);
    setRolesPendingSlug(null);
    if (result.error) {
      setRolesError(result.error);
      return;
    }
    if (result.data && rolesData) {
      const nextGrants = [
        ...rolesData.grants,
        {
          role_slug: result.data.role_slug,
          granted_by_staff_id: result.data.granted_by_staff_id,
          created_at: result.data.created_at,
        },
      ];
      setRolesData({ ...rolesData, grants: nextGrants });
      syncMemberGrants(member.id, nextGrants.map((g) => g.role_slug));
      setAddRoleValue('');
    }
  };

  const handleRevokeRole = async (
    member: AdminStaffUser,
    roleSlug: string,
  ): Promise<void> => {
    setRolesError(null);
    setRolesPendingSlug(roleSlug);
    const result = await adminStaffApi.revokeRole(apiUrl, member.id, roleSlug);
    setRolesPendingSlug(null);
    if (result.error) {
      setRolesError(result.error);
      return;
    }
    if (rolesData) {
      const nextGrants = rolesData.grants.filter((g) => g.role_slug !== roleSlug);
      setRolesData({ ...rolesData, grants: nextGrants });
      syncMemberGrants(member.id, nextGrants.map((g) => g.role_slug));
    }
  };

  if (isLoading) {
    return (
      <section className="ferro-admin-page">
        <h1 className="ferro-section__title">Staff Management</h1>
        <div className="ferro-skeleton-stack" data-testid="staff-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="ferro-skeleton ferro-skeleton--stat" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="ferro-admin-page">
        <h1 className="ferro-section__title">Staff Management</h1>
        <div className="ferro-alert ferro-alert--error ferro-alert--dismissible" role="alert">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void loadStaff()}
            className="ferro-alert__dismiss"
          >
            Try again
          </button>
        </div>
      </section>
    );
  }

  // `withTestId` keeps the data-testid unique in the DOM: the table and
  // card views both render (CSS toggles visibility), so only the table
  // copy carries the hook Playwright/vitest target.
  const renderStatusPill = (member: AdminStaffUser, withTestId = false): JSX.Element => (
    <span
      className={`ferro-status-pill ${
        member.is_active ? 'ferro-status-pill--success' : 'ferro-status-pill--danger'
      }`}
      data-testid={withTestId ? `status-${member.id}` : undefined}
    >
      {member.is_active ? 'Active' : 'Inactive'}
    </span>
  );

  const renderRoleControl = (member: AdminStaffUser, isSelf: boolean): JSX.Element =>
    isSelf || !canManageStaff ? (
      <span className={rolePillClass(member.role)}>{roleLabel(member.role)}</span>
    ) : (
      <select
        value={member.role}
        onChange={(e) => void handleRoleChange(member.id, e.target.value)}
        aria-label={`Role for ${member.first_name} ${member.last_name}`}
        className="ferro-role-select"
      >
        {ROLE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );

  // #428 — additive-role chips under the primary role. Display-only in
  // the roster (grant/revoke lives in the Manage-roles modal); the "+"
  // prefix reads as additive at a glance. A member without grants renders
  // nothing here — the primary pill is the whole truth.
  const renderGrantChips = (member: AdminStaffUser): JSX.Element | null => {
    const grants = member.granted_roles ?? [];
    if (grants.length === 0) return null;
    return (
      <div className="ferro-role-grants" data-testid={`grants-${member.id}`}>
        {grants.map((slug) => (
          <span key={slug} className={rolePillClass(slug)}>
            + {roleLabel(slug)}
          </span>
        ))}
      </div>
    );
  };

  const renderRolesButton = (member: AdminStaffUser, block = false): JSX.Element => (
    <button
      type="button"
      onClick={() => openRolesEditor(member)}
      className={`ferro-btn ferro-btn--sm ferro-btn--outline${block ? ' ferro-btn--block' : ''}`}
      aria-label={`Manage roles for ${member.first_name} ${member.last_name}`}
    >
      Roles
    </button>
  );

  const renderToggleButton = (member: AdminStaffUser, block = false): JSX.Element => (
    <button
      type="button"
      onClick={() => void handleToggleActive(member)}
      disabled={pendingId === member.id}
      className={`ferro-btn ferro-btn--sm ${
        member.is_active ? 'ferro-btn--danger' : 'ferro-btn--outline'
      }${block ? ' ferro-btn--block' : ''}`}
      aria-label={
        member.is_active
          ? `Deactivate ${member.first_name} ${member.last_name}`
          : `Reactivate ${member.first_name} ${member.last_name}`
      }
    >
      {pendingId === member.id ? '…' : member.is_active ? 'Deactivate' : 'Reactivate'}
    </button>
  );

  return (
    <section className="ferro-admin-page">
      <PageHeader
        title="Staff Management"
        subtitle={`${staffList.length} team member${staffList.length !== 1 ? 's' : ''}`}
        actions={
          canManageStaff ? (
            <button
              type="button"
              onClick={() => {
                setShowInviteForm(true);
                setInviteError(null);
                setInviteSuccess(null);
              }}
              className="ferro-btn ferro-btn--primary"
            >
              Invite Staff
            </button>
          ) : undefined
        }
      />

      {inviteSuccess ? (
        <div
          className="ferro-alert ferro-alert--success ferro-alert--dismissible"
          role="status"
        >
          <span>{inviteSuccess}</span>
          <button
            type="button"
            onClick={() => setInviteSuccess(null)}
            className="ferro-alert__dismiss"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {roleError ? (
        <div
          className="ferro-alert ferro-alert--error ferro-alert--dismissible"
          role="alert"
        >
          <span>Role update failed: {roleError}</span>
          <button
            type="button"
            onClick={() => setRoleError(null)}
            className="ferro-alert__dismiss"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {actionError ? (
        <div
          className="ferro-alert ferro-alert--error ferro-alert--dismissible"
          role="alert"
        >
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="ferro-alert__dismiss"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* A5 — Pending invites (invited, not yet accepted). Hidden when
          empty: the page-level "Invite Staff" CTA is the empty-state
          action. Renders at every width (compact cards). */}
      {canManageStaff && (pendingInvites.length > 0 || invitesError) ? (
        <section
          className="ferro-invite-section"
          aria-label={t('admin.staff.pendingInvites')}
        >
          <h2 className="ferro-subsection__title">
            {t('admin.staff.pendingInvites')}
          </h2>
          {invitesError ? (
            <div
              className="ferro-alert ferro-alert--error ferro-alert--dismissible"
              role="alert"
            >
              <span>{t('admin.staff.invitesLoadError')}</span>
              <button
                type="button"
                onClick={() => void loadInvites()}
                className="ferro-alert__dismiss"
              >
                {t('admin.staff.tryAgain')}
              </button>
            </div>
          ) : (
            <div className="ferro-invite-list">
              {pendingInvites.map((invite) => {
                const expired = isInviteExpired(invite);
                const lastSend = lastSendLine(invite);
                return (
                  <div key={invite.id} className="ferro-staff-card">
                    <div className="ferro-staff-card__top">
                      <div>
                        <h3 className="ferro-staff-card__name">
                          {invite.first_name} {invite.last_name}
                        </h3>
                        <p className="ferro-staff-card__meta">{invite.email}</p>
                        {lastSend ? (
                          <p
                            className={`ferro-staff-card__meta ${
                              lastSend.failed
                                ? 'ferro-staff-card__meta--danger'
                                : ''
                            }`}
                            data-testid={`invite-last-send-${invite.id}`}
                          >
                            {t(lastSend.key, { when: lastSend.when })}
                          </p>
                        ) : null}
                      </div>
                      <span
                        className={`ferro-status-pill ${
                          expired
                            ? 'ferro-status-pill--danger'
                            : 'ferro-status-pill--pending'
                        }`}
                        data-testid={`invite-status-${invite.id}`}
                      >
                        {expired
                          ? t('admin.staff.inviteExpired')
                          : t('admin.staff.invited')}
                      </span>
                    </div>
                    <div className="ferro-staff-card__row">
                      <span className={rolePillClass(invite.role_slug)}>
                        {roleLabel(invite.role_slug)}
                      </span>
                      <button
                        type="button"
                        onClick={() => void handleResendInvite(invite)}
                        disabled={
                          resendingEmail !== null || revokingId !== null
                        }
                        className="ferro-btn ferro-btn--sm ferro-btn--outline"
                        aria-label={`${t('admin.staff.resendInvite')} — ${invite.email}`}
                      >
                        {resendingEmail === invite.email
                          ? t('admin.staff.resendingInvite')
                          : t('admin.staff.resendInvite')}
                      </button>
                      {/* #666 — two-tap revoke. Armed state turns the
                          button danger-coloured and relabels it, so the
                          destructive step is explicit rather than a
                          hover tooltip the touch users never see. */}
                      <button
                        type="button"
                        onClick={() => void handleRevokeInvite(invite)}
                        disabled={
                          revokingId !== null || resendingEmail !== null
                        }
                        className={`ferro-btn ferro-btn--sm ${
                          revokeArmedId === invite.id
                            ? 'ferro-btn--danger'
                            : 'ferro-btn--outline'
                        }`}
                        aria-label={`${t('admin.staff.revokeInvite')} — ${invite.email}`}
                      >
                        {revokingId === invite.id
                          ? t('admin.staff.revokingInvite')
                          : revokeArmedId === invite.id
                            ? t('admin.staff.revokeInviteConfirm')
                            : t('admin.staff.revokeInvite')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : null}

      {/* Desktop table (≥768px) */}
      <div className="ferro-staff-table-view ferro-table-card">
        <div className="ferro-table-scroll">
          <table className="ferro-admin-table" aria-label="Staff list">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last login</th>
                <th>Joined</th>
                {canManageStaff ? <th className="ferro-admin-table__right">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {staffList.length === 0 ? (
                <tr>
                  <td
                    colSpan={canManageStaff ? 7 : 6}
                    className="ferro-admin-table__empty"
                  >
                    No staff members yet.
                  </td>
                </tr>
              ) : null}
              {staffList.map((member) => {
                const isSelf = currentStaff?.id === member.id;
                return (
                  <tr key={member.id}>
                    <td>
                      <span className="ferro-admin-table__primary">
                        {member.first_name} {member.last_name}
                      </span>
                      {isSelf ? <span className="ferro-you-tag"> (you)</span> : null}
                    </td>
                    <td>{member.username}</td>
                    <td>
                      {renderRoleControl(member, isSelf)}
                      {renderGrantChips(member)}
                    </td>
                    <td>{renderStatusPill(member, true)}</td>
                    <td>{formatLastLogin(member.last_login_at)}</td>
                    <td>{new Date(member.created_at).toLocaleDateString()}</td>
                    {canManageStaff ? (
                      <td className="ferro-admin-table__right">
                        {!isSelf ? (
                          <>
                            {renderRolesButton(member)}{' '}
                            {renderToggleButton(member)}
                          </>
                        ) : (
                          <span className="ferro-muted">—</span>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards (<768px) */}
      <div className="ferro-staff-cards-view">
        {staffList.length === 0 ? (
          <p className="ferro-centered-note">No staff members yet.</p>
        ) : null}
        {staffList.map((member) => {
          const isSelf = currentStaff?.id === member.id;
          return (
            <div key={member.id} className="ferro-staff-card">
              <div className="ferro-staff-card__top">
                <div>
                  <h3 className="ferro-staff-card__name">
                    {member.first_name} {member.last_name}
                    {isSelf ? <span className="ferro-you-tag">(you)</span> : null}
                  </h3>
                  <p className="ferro-staff-card__meta">@{member.username}</p>
                </div>
                {renderStatusPill(member)}
              </div>
              <p className="ferro-staff-card__meta">
                Last login: {formatLastLogin(member.last_login_at)}
              </p>
              <div className="ferro-staff-card__row">
                <span className="ferro-staff-card__meta">
                  Joined {new Date(member.created_at).toLocaleDateString()}
                </span>
                {renderRoleControl(member, isSelf)}
              </div>
              {renderGrantChips(member)}
              {canManageStaff && !isSelf ? (
                <>
                  {renderRolesButton(member, true)}
                  {renderToggleButton(member, true)}
                </>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* #428 — Manage-roles editor (owner-only). One member at a time;
          the modal owns loading/empty/error/pending states per the design
          philosophy's five-state rule. Stale doesn't apply: grants can't
          age mid-session — every open refetches. */}
      <Modal
        isOpen={rolesFor !== null}
        onClose={closeRolesEditor}
        title={
          rolesFor
            ? `Roles — ${rolesFor.first_name} ${rolesFor.last_name}`
            : 'Roles'
        }
      >
        {rolesFor ? (
          <div data-testid="roles-editor">
            {rolesError ? (
              <div
                className="ferro-alert ferro-alert--error ferro-alert--dismissible"
                role="alert"
              >
                <span>{rolesError}</span>
                <button
                  type="button"
                  onClick={() => void loadRoles(rolesFor)}
                  className="ferro-alert__dismiss"
                >
                  Try again
                </button>
              </div>
            ) : null}

            {rolesLoading ? (
              <div className="ferro-skeleton-stack" data-testid="roles-skeleton">
                <div className="ferro-skeleton ferro-skeleton--stat" />
              </div>
            ) : rolesData ? (
              <>
                <div className="ferro-roles-modal__section">
                  <h3 className="ferro-subsection__title">Primary role</h3>
                  <span className={rolePillClass(rolesData.primary_role)}>
                    {roleLabel(rolesData.primary_role)}
                  </span>
                </div>

                <div className="ferro-roles-modal__section">
                  <h3 className="ferro-subsection__title">Additional roles</h3>
                  {rolesData.grants.length === 0 ? (
                    <p className="ferro-muted">
                      No additional roles yet. Grant one so this person can
                      work another station without a second account.
                    </p>
                  ) : (
                    rolesData.grants.map((grant) => (
                      <div key={grant.role_slug} className="ferro-role-grant-row">
                        <span className={rolePillClass(grant.role_slug)}>
                          + {roleLabel(grant.role_slug)}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            void handleRevokeRole(rolesFor, grant.role_slug)
                          }
                          disabled={rolesPendingSlug !== null}
                          className="ferro-btn ferro-btn--sm ferro-btn--danger"
                          aria-label={`Remove ${roleLabel(grant.role_slug)} from ${rolesFor.first_name} ${rolesFor.last_name}`}
                        >
                          {rolesPendingSlug === grant.role_slug ? '…' : 'Remove'}
                        </button>
                      </div>
                    ))
                  )}

                  {(() => {
                    const taken = new Set<string>([
                      rolesData.primary_role,
                      ...rolesData.grants.map((g) => g.role_slug),
                    ]);
                    const options = ROLE_OPTIONS.filter(
                      (opt) => isGrantableRoleSlug(opt.value) && !taken.has(opt.value),
                    );
                    if (options.length === 0) return null;
                    return (
                      <div className="ferro-role-grant-add">
                        <select
                          value={addRoleValue}
                          onChange={(e) => setAddRoleValue(e.target.value)}
                          aria-label={`Add a role for ${rolesFor.first_name} ${rolesFor.last_name}`}
                          className="ferro-role-select"
                        >
                          <option value="">Add a role…</option>
                          {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => void handleGrantRole(rolesFor)}
                          disabled={
                            rolesPendingSlug !== null ||
                            !isGrantableRoleSlug(addRoleValue)
                          }
                          className="ferro-btn ferro-btn--sm ferro-btn--primary"
                        >
                          {rolesPendingSlug !== null &&
                          rolesPendingSlug === addRoleValue
                            ? 'Granting…'
                            : 'Grant'}
                        </button>
                      </div>
                    );
                  })()}
                </div>

                <p className="ferro-roles-modal__note">
                  Role changes sign {rolesFor.first_name} out everywhere —
                  their new access starts at their next sign-in.
                </p>
              </>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={showInviteForm}
        onClose={() => setShowInviteForm(false)}
        title="Invite staff member"
      >
        <StaffForm
          roleOptions={ROLE_OPTIONS}
          initialRole={DEFAULT_INVITE_ROLE}
          isSubmitting={isSubmitting}
          error={inviteError}
          onCancel={() => setShowInviteForm(false)}
          onSubmit={(values) => void handleInvite(values)}
        />
      </Modal>
    </section>
  );
}
